import {useMemo} from 'react'
import {trimDiffLineText, getContentFromLines, isContextDiffLine, isDiffLine} from '../helpers/line-helpers'
import type {DiffLine} from '../types'
import {useAnalytics} from '@github-ui/use-analytics'
import {useStableCallback} from '@github-ui/use-stable-callback'
import type {LineRange} from '@github-ui/diffs/types'
import {useGetSelectedLineRange, useGetSplitDiffLinesFromLineRange} from '../stores/selectors'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useHasCommitRange} from '@github-ui/pull-requests/pull-request-store-selectors'

/**
 * Returns the configuration for the Suggest Changes button within the markdown toolbar
 */
export function useSuggestedChanges(
  line: DiffLine,
  shouldStartNewConversationWithSuggestedChange: boolean | undefined,
) {
  const {sendAnalyticsEvent} = useAnalytics()
  const getSplitDiffLinesFromLineRange = useGetSplitDiffLinesFromLineRange()
  const hasCommitRange = useHasCommitRange()

  const getSelectedLineRange = useGetSelectedLineRange()
  const {isServer: isSSR} = useRuntimeEnvironment()

  /**
   * Validates whether the given line range is valid for suggesting changes.
   * A valid suggestion range must not contain deletion lines.
   *
   *
   * @param rowRange - The range of lines to validate
   * @returns true if the range is valid for suggestions, false otherwise
   */
  function isValidSuggestionRange(rowRange?: LineRange): boolean {
    // Context menu triggers from single line don't have a row range, so fall back to the line
    if (!rowRange) {
      return line.type !== 'DELETION'
    }
    // Single line selections are valid if they're not a deleted line
    const singleLine =
      rowRange.startLineNumber === rowRange.endLineNumber && rowRange.startOrientation === rowRange.endOrientation
    if (singleLine) {
      return line.type !== 'DELETION'
    }

    const rightSideOnly = rowRange.startOrientation === 'right' && rowRange.endOrientation === 'right'
    const firstSelectedRightLineFromRange = getSplitDiffLinesFromLineRange(rowRange)?.selectedRightLines[0]

    const firstSelectedLineIsContext =
      firstSelectedRightLineFromRange &&
      isDiffLine(firstSelectedRightLineFromRange) &&
      isContextDiffLine(firstSelectedRightLineFromRange)

    // Right-side-only selections are always valid unless the first selected line is a context line
    if (rightSideOnly && !firstSelectedLineIsContext) {
      return true
    }

    // All other cases need to check for deletions:
    // - Right-side-only with context line
    // - Left-side-only
    // - Cross-side selections (startsRightEndsLeft, startsLeftEndsRight)
    // - Other cases (e.g., unified view)
    return checkForDeletions(rowRange)
  }

  function checkForDeletions(rowRange: LineRange): boolean {
    const selectedLeftLinesFromRange = getSplitDiffLinesFromLineRange(rowRange)?.selectedLeftLines
    // If there are left lines (even for right-side selections), check they don't contain deletions
    // This prevents suggesting changes when there are deleted lines in the range
    if (selectedLeftLinesFromRange && selectedLeftLinesFromRange.length > 0) {
      return selectedLeftLinesFromRange.every(
        diffLine => diffLine !== 'empty-diff-line' && diffLine.type !== 'DELETION',
      )
    }
    return true
  }

  /**
   *
   * Returns the lines that should be suggested for a comment
   *
   * @param diffLines - the right lines belonging to the range for the thread or the new conversation
   * @param line - the current line the cell from which this is called belongs to
   *
   */
  function getSourceContentDiffLinesFromRowRange(rowRange: LineRange | undefined): string | undefined {
    const diffLines = rowRange ? getSplitDiffLinesFromLineRange(rowRange)?.selectedRightLines : []

    if (rowRange && diffLines && diffLines.length > 1) {
      // Don't attempt getting line content with getContentFromLines function as it uses DOMParser, which does not work with Alloy, while in SSR.
      if (isSSR) return ''
      return getContentFromLines(diffLines)
    }

    if (!line) return ''
    // Single line
    const [lineContent] = trimDiffLineText(line.text, line.type)
    return lineContent
  }

  /**
   * Returns the configuration for the Suggest Changes button within the markdown toolbar
   *
   * @param rowRange - the range of lines for the thread or the new conversation
   * @param shouldInsertSuggestedChange - boolean indicating whether to auto insert the suggested change on render
   */
  // eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
  const configureSuggestedChangesFromLineRange = useStableCallback(function configureSuggestedChangesFromLineRange(
    rowRange?: LineRange,
    shouldInsertSuggestedChange?: boolean,
  ) {
    const showSuggestChangesButton = !!(isValidSuggestionRange(rowRange) && !hasCommitRange)
    return {
      isValidSuggestionRange: showSuggestChangesButton,
      showSuggestChangesButton,
      sourceContentFromDiffLines: showSuggestChangesButton
        ? getSourceContentDiffLinesFromRowRange(rowRange)
        : undefined,
      onInsertSuggestedChange: () => {
        sendAnalyticsEvent('diff.add_suggested_change', 'ADD_SUGGESTED_CHANGE_BUTTON')
      },
      shouldInsertSuggestedChange,
    }
  })

  return useMemo(() => {
    return {
      getSelectedLineRange,
      configureSuggestedChangesFromLineRange,
      shouldStartNewConversationWithSuggestedChange,
    }
  }, [configureSuggestedChangesFromLineRange, getSelectedLineRange, shouldStartNewConversationWithSuggestedChange])
}
